import { AdditiveBlending, BoxGeometry, Color, DoubleSide, Mesh, ShaderMaterial } from "three";

// Warp gate: additive energy walls rising from the floor and fading upward,
// bright vertical corner edges and a pulsing floor rim. Works in object space
// (the ±1 × 0..2 box) so it looks the same at any IFO scale.
const WARP_VERT = /* glsl */ `
varying vec3 vPos;
varying vec3 vNormal;
void main() {
    vPos = position;
    vNormal = normal;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}`;

const WARP_FRAG = /* glsl */ `
uniform float uTime;
uniform vec3 uColor;
varying vec3 vPos;
varying vec3 vNormal;
void main() {
    if (vNormal.z > 0.5) discard; // open top
    float a;
    if (vNormal.z < -0.5) {
        // Floor: glowing rim plus rings pulsing outward.
        float d = max(abs(vPos.x), abs(vPos.y));
        float rings = pow(0.5 + 0.5 * sin(d * 18.0 - uTime * 4.0), 6.0);
        a = smoothstep(0.75, 1.0, d) * 0.8 + rings * 0.25;
    } else {
        float h = vPos.z * 0.5;                      // 0 floor .. 1 top
        float side = abs(vNormal.x) > 0.5 ? vPos.y : vPos.x;
        float bands = pow(0.5 + 0.5 * sin(h * 24.0 - uTime * 5.0), 4.0);
        float corner = smoothstep(0.85, 1.0, abs(side));
        float fade = pow(1.0 - h, 1.5);
        a = fade * (0.15 + 0.6 * bands + 0.7 * corner);
    }
    gl_FragColor = vec4(uColor * a, a);
}`;

export const WARP_GATE_COLOR = 0x33ddff;

/**
 * Animated warp-gate volume. Unit-sized (±1 footprint, 0..2 height) — apply the
 * IFO position/rotation/scale to it so it matches findWarpContact's box.
 */
export function createWarpGateMesh(color = WARP_GATE_COLOR): Mesh {
    const mat = new ShaderMaterial({
        uniforms: { uTime: { value: 0 }, uColor: { value: new Color(color) } },
        vertexShader: WARP_VERT,
        fragmentShader: WARP_FRAG,
        transparent: true,
        blending: AdditiveBlending,
        depthWrite: false,
        side: DoubleSide,
    });
    const mesh = new Mesh(new BoxGeometry(2, 2, 2).translate(0, 0, 1), mat);
    mesh.name = "WARP_GATE";
    mesh.onBeforeRender = () => { mat.uniforms.uTime.value = performance.now() / 1000; };
    return mesh;
}
